import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import _, { identity } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'

import styles from '../ReassignDonationModal.module.scss'

import { ReassignAllMarkedDonationsModalProps } from './ReassignAllMarkedDonationsModal.types'
import { useMutation, useQueryList } from '@percent/admin-dashboard/common/hooks'
import {
  AcknowledgeModal,
  ActionModal,
  Alert,
  AsyncSelect,
  ConfirmationModal,
  FormField,
  Modal,
  Select,
  Text,
  TextInput,
  SelectOption
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import countries from '@percent/admin-dashboard/i18n/data/countries'
import { createShortLink } from '@percent/utility'
import i18n from '@percent/admin-dashboard/i18n/config'
import { Organisation } from '@percent/admin-dashboard/api/types'
import { ReassignmentKind } from '@percent/admin-dashboard/api/actions/donations/donations.types'
import { isApiError } from '@percent/admin-dashboard/common/utility/isApiError'
import { isStringDefined } from '@percent/admin-dashboard/common/utility/isDefined'

const THROTTLING_TIME = 500

const reasons = {
  [ReassignmentKind.Unpayable]: [
    'Undeliverable - bank transfer fail',
    'Undeliverable - card payment fail',
    'Undeliverable - cheque returned',
    'Undeliverable - compliance fail',
    'Organisation was set incorrectly'
  ],
  other: 'Other'
}

const failedReassignmentReasons = {
  cancelled: 'Cancelled',
  disbursed: 'Disbursed',
  inActiveDisbursement: 'In active disbursement',
  ineligibleOrganisation: 'Ineligible organisation',
  mismatchedFoundations: 'Mismatched foundations',
  missingDonationReassignment: 'Missing donation reassignment',
  missingOrganisationId: 'Missing organisation ID',
  missingReason: 'Missing reason'
}

const createOptions = () => {
  const allReasons = reasons[ReassignmentKind.Unpayable].concat([reasons.other])

  return allReasons.map(reason => ({
    label: reason,
    value: reason
  }))
}

export function ReassignAllMarkedDonationsModal({
  open,
  onClose,
  refresh,
  sourceOrganisationName,
  currentOrganisationId
}: ReassignAllMarkedDonationsModalProps) {
  const { t } = useTranslation()
  const { adminService, donationsService } = useServices()
  const options = createOptions()
  const shortLanguage = i18n.language.split('-')[0]

  const [query, setQuery] = useState<string>('')
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [successDialogState, setSuccessDialogState] = useState(false)
  const [errorDialogState, setErrorDialogState] = useState(false)
  const [selectedReasonOption, setSelectedReasonOption] = useState<SelectOption | undefined>(undefined)
  const [selectedOrganisationName, setSelectedOrganisationName] = useState<string | null | undefined>(undefined)
  const [failedReassignments, setFailedReassignments] = useState<string[]>([])
  const [reassignedCount, setReassignedCount] = useState<number | undefined>(undefined)

  const [
    { isLoading: isReassignMarkedDonationsLoading, error: reassignMarkedDonationsError },
    { apiFunc: reassignMarkedDonationsApiFunc }
  ] = useMutation(
    donationsService.reassignMarkedDonations,
    ({ data: { data } }) => {
      setFailedReassignments(
        Object.entries(data.ineligibleDonations)
          .filter(([, value]) => value)
          .map(
            ([key, value]) => `${failedReassignmentReasons[key as keyof typeof failedReassignmentReasons]}: ${value}`
          )
      )

      setReassignedCount(data.reassignedCount)

      if (data.reassignedCount) {
        setSuccessDialogState(true)
      } else {
        setErrorDialogState(true)
      }
    },
    () => setErrorDialogState(true)
  )

  const [{ dataOrNull: organisationsData, isLoading: isOrganisationsLoading }, { query: organisationsQuery }] =
    useQueryList(adminService.getOrganisations, {
      pageSize: 25
    })

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      reassignedReason: '',
      customReason: '',
      organisationId: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        reassignedReason: Yup.string().oneOf(Object.values(reasons).flat()),
        organisationId: Yup.string().trim(),
        customReason: Yup.string()
          .nullable()
          .optional()
          .when('reassignedReason', {
            is: 'Other',
            then: Yup.string()
              .trim()
              .nullable()
              .min(1, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Custom reason' }))
              .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Custom reason' }))
              .required('reassignment.customReason.required')
          })
      }),
    onSubmit: async ({ reassignedReason, organisationId, customReason }) => {
      const reason = reassignedReason === reasons.other ? customReason : reassignedReason

      await reassignMarkedDonationsApiFunc({
        organisationId: currentOrganisationId,
        payload: {
          reason: isStringDefined(reason) ? reason : undefined,
          newOrganisationId: isStringDefined(organisationId) ? organisationId : undefined
        }
      })
    }
  })

  const {
    touched,
    errors,
    setFieldTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    setValues,
    handleSubmit,
    isValid,
    resetForm,
    values
  } = formik

  const handleCloseModal = () => {
    setShowConfirmationModal(false)
    onClose()
    refresh()
    resetForm()
    setSelectedOrganisationName(null)
    setSuccessDialogState(false)
  }

  const handleOnErrorClose = () => {
    setErrorDialogState(false)
  }

  const getOrganisationDescription = useCallback(
    (organisation: Organisation): string => {
      return [
        organisation.website ? createShortLink(organisation.website) : undefined,
        `${t('typography.id')}: ${organisation.registryId}`,
        countries.getName(organisation.countryCode, shortLanguage)
      ]
        .filter(identity)
        .join(' | ')
    },
    [shortLanguage, t]
  )

  const searchResults: SelectOption[] = useMemo(() => {
    const organisations = (organisationsData ?? []).filter(o => !!o)

    return organisations
      ? organisations.map(organisation => ({
          value: organisation.id,
          label: organisation.name,
          description: getOrganisationDescription(organisation)
        }))
      : []
  }, [organisationsData, getOrganisationDescription])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOrganisationsQuery = useCallback(
    _.debounce<any>((filter: string) => {
      organisationsQuery({ query: filter })
    }, THROTTLING_TIME),
    []
  )
  useEffect(() => {
    if (query) {
      debouncedOrganisationsQuery(query)
    }
  }, [query, debouncedOrganisationsQuery])

  const successOrErrorModal =
    successDialogState && !errorDialogState ? (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.reassignDonation.success.title')}
        description={
          !failedReassignments.length && (
            <Trans
              i18nKey={
                selectedOrganisationName
                  ? 'dialog.reassignMarkedDonations.success.description.targetOrg'
                  : 'dialog.reassignMarkedDonations.success.description'
              }
              values={{ reassignedCount, orgName: selectedOrganisationName, plural: reassignedCount === 1 ? '' : 's' }}
              components={{ b: <strong /> }}
            />
          )
        }
        buttonText={t('button.close')}
        handleClose={handleCloseModal}
        viewTestId="success-donation-reassign-modal"
      >
        {!!failedReassignments.length && (
          <div className={styles.errorPage}>
            <Text size="medium">
              <Trans
                i18nKey="dialog.reassignMarkedDonations.success.failedReassignments.title"
                values={{ reassignedCount, plural: reassignedCount === 1 ? '' : 's' }}
              />
            </Text>
            <div className={styles.errorDetails}>
              <ul>
                {failedReassignments.map((reason, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`reason-${i}`}>
                    <Text size="small">{reason}</Text>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </AcknowledgeModal>
    ) : (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.reassignDonation.error.title')}
        buttonText={t('button.close')}
        handleClose={handleOnErrorClose}
        viewTestId="error-donation-reassign-modal"
      >
        <div className={styles.errorPage}>
          <Text size="medium">
            {failedReassignments.length
              ? t('dialog.reassignMarkedDonations.fail.failedReassignments.title.none')
              : t('dialog.reassignMarkedDonations.error.description')}
          </Text>
          {reassignMarkedDonationsError && isApiError(reassignMarkedDonationsError) && (
            <div className={styles.errorDetails}>
              <Text size="small">{reassignMarkedDonationsError.message}</Text>
              {reassignMarkedDonationsError.reasons && (
                <ul>
                  {reassignMarkedDonationsError.reasons.map((reason, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`reason-${i}`}>
                      <Text size="small">{reason}</Text>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {!!failedReassignments.length && (
            <div className={styles.errorDetails}>
              <ul>
                {failedReassignments.map((reason, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`reason-${i}`}>
                    <Text size="small">{reason}</Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </AcknowledgeModal>
    )

  return (
    <Modal open={open} onClose={handleCloseModal} aria-labelledby="reassign-marked-donations-form-modal">
      {!successDialogState && !errorDialogState ? (
        showConfirmationModal ? (
          <ConfirmationModal
            title={t('dialog.reassignDonation.confirmation.title')}
            loading={isReassignMarkedDonationsLoading}
            primaryButtonText={t('button.reassign')}
            primaryBtnTestId="confirm-donation-reassignment"
            secondaryButtonText={t('button.cancel')}
            handleSubmit={handleSubmit}
            handleClose={() => setShowConfirmationModal(false)}
          >
            <Trans
              i18nKey="dialog.reassignMarkedDonations.confirmation.description"
              values={{
                orgA: sourceOrganisationName,
                target: selectedOrganisationName || t('dialog.reassignMarkedDonations.confirmation.targetBlank')
              }}
              components={{ b: <strong /> }}
            />
          </ConfirmationModal>
        ) : (
          <ActionModal
            title={t('dialog.reassignDonations.title')}
            primaryButtonText={t('button.reassign')}
            secondaryButtonText={t('button.cancel')}
            type="submit"
            variant={isValid ? 'primary' : 'secondary'}
            disabled={!(isValid && !isReassignMarkedDonationsLoading)}
            loading={isReassignMarkedDonationsLoading}
            handleClose={handleCloseModal}
            handleSubmit={() => setShowConfirmationModal(true)}
            primaryBtnTestId="reassign-donation-submit-button"
            secondaryBtnTestId="reassign-donation-cancel-button"
          >
            <div className={styles.reassignmentKind}>
              <div className={styles.withFieldGap}>
                <Alert variant="info" title={t('dialog.reassignMarkedDonations.alert.title')}>
                  <Trans
                    i18nKey="dialog.reassignMarkedDonations.alert.description"
                    values={{ type: t('typography.reassignmentKind.unpayable') }}
                    components={{ b: <strong /> }}
                  />
                </Alert>
              </div>
              <Text size="small" fontWeight="bold" dataTestId="typeOfReassignmentLabel">
                {t('dialog.reassignDonation.typeOfReassignment.alert.title')}:{' '}
              </Text>
              <Text size="small" dataTestId="typeOfReassignmentValue">
                {t('typography.reassignmentKind.unpayable')}
              </Text>
            </div>
            <form>
              <div className={styles.withFieldGap}>
                <FormField label={t('typography.newOrganisation')} data-testid="organisationId" necessity="optional">
                  <AsyncSelect
                    name="organisationId"
                    placeholder={t('typography.search.organisation')}
                    onChange={e => {
                      setValues({
                        ...values,
                        organisationId: e?.value || ''
                      })
                      setSelectedOrganisationName(e?.label)
                    }}
                    options={searchResults}
                    setQuery={e => {
                      setQuery(e)
                      setFieldTouched('organisationId')
                    }}
                    query={query}
                    loading={isOrganisationsLoading}
                    loadingText="loading"
                    noResultsFoundText={t('errorMessage.noResult')}
                    data-testid="organisationSearch"
                    defaultValue={searchResults.find(option => option.value === values.organisationId)}
                  />
                </FormField>
              </div>
              <div className={styles.withFieldGap}>
                <FormField
                  label={t('dialog.reassignDonation.reassignedReason')}
                  data-testid="reassignedReason"
                  necessity="optional"
                >
                  <Select
                    placeholder={t('dialog.reassignDonation.reassignedReason')}
                    options={options}
                    onChange={({ value }) => {
                      setFieldValue('reassignedReason', value)
                      setSelectedReasonOption(options.find(option => option.value === value))
                    }}
                    searchable={false}
                    defaultValue={selectedReasonOption}
                  />
                </FormField>
              </div>
              {values.reassignedReason === reasons.other && (
                <FormField
                  label={t('dialog.reassignDonation.customReason')}
                  necessity="optional"
                  status={touched.customReason && errors.customReason ? 'danger' : 'default'}
                  statusMessage={errors.customReason || ''}
                  data-testid="customReason"
                >
                  <TextInput
                    name="customReason"
                    value={values.customReason || ''}
                    placeholder={t('dialog.reassignDonation.customReason')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
              )}
            </form>
          </ActionModal>
        )
      ) : (
        successOrErrorModal
      )}
    </Modal>
  )
}
